/**
 * Rail-sized live-run card. `RunTriageLockup` is a wide horizontal layout
 * (results column + triage panel) built for the full page width and shared
 * with three detail pages — it does not fit a ~320px rail, and restyling it
 * there would touch every one of its other consumers. This is a narrower,
 * vertically-stacked sibling that reuses the
 * same data (`RunSummaryData` from `normalizeFromStream`) and primitives
 * (`StatusIconBar`, `Progress`) instead of the dropdown/gauge pairing.
 *
 * @module ui/pages/runs/test-runs/LiveRunCard
 */
import { Button, Card, Flex, Heading, Progress, StatusIconBar, Text, cn, type StatusIconBarItem } from '@chatsdet/ui';
import type { RunSummaryData } from '../../../../features/workflows/useRunSummaryData';
import { computeRunTotals } from '../../../../features/workflows/runTotals';
import type { ConnectionState } from '../../../../hooks/useWorkflowTestStream';
import { MATCH_LOCKUP_BORDER } from './matchLockupBorder';
import { formatRelativeStartTime } from './relativeStartTime';

export type LiveRunCardProps = {
  data: RunSummaryData;
  /** `runTarget.label` for the run this card shows, or null before discovery
   * has resolved it (see `useLiveWorkflowTestExecutions`'s `addRun`). */
  label: string | null;
  startedAt: string | null;
  connectionState?: ConnectionState;
  onRetry?: () => void;
  /** When provided, clicking a result square fires this with the full item. */
  onItemSelect?: (item: StatusIconBarItem) => void;
  /**
   * True when a `LiveRunTabs` strip sits flush above this card (more than
   * one live run) — drops the card's own top border/rounding so the
   * selected tab and the card read as one continuous object, the same
   * lockup technique the filter bar/sparkline/table use elsewhere on this
   * page (`rounded-t-none border-t-0`).
   */
  attached?: boolean;
};

export function LiveRunCard({
  data,
  label,
  startedAt,
  connectionState,
  onRetry,
  onItemSelect,
  attached = false,
}: LiveRunCardProps) {
  const { settled, passRate } = computeRunTotals(data.items);
  // Same rule as `RunTriageLockup` (an allowlist of the
  // in-progress statuses, not a `!== 'completed'` denylist) — a completed run
  // stays in the rail's `runs` list for ~30s after finishing (see the cleanup
  // effect in `useLiveWorkflowTestExecutions`), and during that window it must
  // not still show a pulsing "live" indicator.
  const isLive = data.status === 'running' || data.status === 'queued';

  return (
    <Card padding="md" className={cn(MATCH_LOCKUP_BORDER, attached && 'rounded-t-none border-t-0')}>
      {connectionState === 'failed' && (
        <Flex
          align="center"
          gap="sm"
          className="mb-3 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
        >
          <Text size="xs" className="text-amber-800 dark:text-amber-200">
            Live updates disconnected.
          </Text>
          {onRetry && (
            <Button variant="ghost" size="sm" onPress={onRetry}>
              Reconnect
            </Button>
          )}
        </Flex>
      )}
      {connectionState === 'reconnecting' && (
        <Flex
          align="center"
          gap="sm"
          className="mb-3 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800"
        >
          <Text size="xs" className="text-blue-800 dark:text-blue-200">
            Reconnecting to live updates...
          </Text>
        </Flex>
      )}

      <Flex align="center" gap="sm">
        <span className="relative flex h-2 w-2 shrink-0">
          {isLive && (
            <span
              className="absolute inline-flex h-full w-full rounded-full bg-green-500"
              style={{ animation: 'subtle-pulse 3s ease-in-out infinite' }}
            />
          )}
          <span
            className={cn(
              'relative inline-flex h-2 w-2 rounded-full',
              isLive ? 'bg-green-500' : 'bg-muted-foreground/40'
            )}
          />
        </span>
        <Text weight="semibold" className="truncate">
          {label ?? 'Test run'}
        </Text>
      </Flex>
      {startedAt && (
        <Text size="xs" className="text-muted-foreground mt-0.5">
          started {formatRelativeStartTime(startedAt)}
        </Text>
      )}

      <Flex align="end" justify="between" className="mt-3">
        <div>
          <Heading level={4} size="2xl" className="tabular-nums leading-none">
            {settled > 0 ? `${passRate}%` : '—'}
          </Heading>
          <Text size="xs" className="text-muted-foreground">
            pass rate
          </Text>
        </div>
        <Text size="sm" className="text-muted-foreground tabular-nums">
          {data.completedWorkflows}/{data.totalWorkflows} complete
        </Text>
      </Flex>
      <Progress
        value={data.totalWorkflows > 0 ? (data.completedWorkflows / data.totalWorkflows) * 100 : 0}
        size="sm"
        className="mt-2"
        aria-label="Run completion"
      />

      <div className="mt-3">
        <StatusIconBar
          items={data.items}
          onItemPress={onItemSelect ? (item) => onItemSelect(item) : undefined}
        />
      </div>
    </Card>
  );
}
